import { Session } from '@utils/types/editors/sessions.ts';
import {
  DocumentSubtitleSegmentV1,
  DocumentSubtitleV1
} from '@utils/types/editors/subtitles/subtitle.ts';
import { DocumentResult } from '@utils/types/editors/generic.ts';
import { DocumentResponse } from '@utils/types/folders/folder.ts';
import { ReactEditor } from 'slate-react';
import { Descendant } from 'slate';
import React from 'react';

// --- Large-document memory guards (Den Haag 11h / ~9k-cue incident) ---
// Undo/redo snapshots are full-document copies; at thousands of cues each is
// many MB, so even a 50-deep stack can blow the mobile per-tab memory ceiling
// (iOS WebKit kills the tab; desktop Chrome's larger budget survives). Scale the
// retained history down as the document grows.
const HISTORY_BASE = 50;
export function getMaxHistory(doc?: DocumentSubtitleV1): number {
  const n = doc?.document?.length ?? 0;
  if (n > 20000) return 3;
  if (n > 8000) return 5;
  if (n > 3000) return 15;
  return HISTORY_BASE;
}

// Highest existing segment id without spreading the whole array into call args:
// Math.max(...arr) risks a call-stack overflow at tens of thousands of items.
export function maxSegmentId(doc?: DocumentSubtitleV1): number {
  let max = 0;
  const segs = doc?.document ?? [];
  for (const s of segs) if (s.id > max) max = s.id;
  return max;
}

export interface CaptionContextType {
  session?: Session;
  document?: DocumentSubtitleV1;
  documentResponse?: DocumentResponse;
  setDocument: (document: DocumentSubtitleV1) => void;
  documentResult?: DocumentResult;
  query: string;
  setQuery: (query: string) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  addListItem: (id: number) => void;
  removeListItem: (id: number) => void;
  splitListItem: (id: number) => void;
  joinListItems: (id: number) => void;
  cleanDocument: () => void;
  saveDocument: (userAction?: boolean) => void;
  searchInputRef?: React.RefObject<HTMLInputElement>;
  clickedIndex: number | null;
  setClickedIndex: (id: number | null) => void;
  cleanup: () => void;
  applyStyle: (
    style: 'bold' | 'italic' | 'underlined' | 'color' | 'strike' | 'position',
    param2?: string
  ) => void;
  applyFontToAll: (fontFamily: string) => void;
  saveDocumentLoading?: boolean;
  documentResetting?: boolean;
  cleanDocumentLoading?: boolean;
  finishedLoading?: boolean;
  changeSegmentTime: (
    id: number,
    field: 'start' | 'stop',
    name: string,
    value: string
  ) => void;
  changeSegmentTimeForCurrentIndex: (
    field: 'start' | 'stop',
    milliseconds: number
  ) => void;
  setTimeAndSegment: (start: number) => void;
  handleMediaElementReady: (waveInfoUrl?: string) => void;
  autoSave: () => void;
  finishDocument: (
    sessionId: string,
    documentId: string,
    newStatus: boolean,
    setFinished: (status: boolean) => void
  ) => void;
  setEditor: (editor: ReactEditor | null) => void;
  editorUpdateSegment: (children: Descendant[]) => void;
  search: string;
  setSearch: (search: string) => void;
  currentMatch: DocumentSubtitleSegmentV1[] | null;
  setCurrentMatch: (match: DocumentSubtitleSegmentV1[] | null) => void;
  caseSensitive: boolean;
  wholeWord: boolean;
  setCaseSensitive: (caseSensitive: boolean) => void;
  setWholeWord: (wholeWord: boolean) => void;
  updateCaptionDocument: (document: DocumentSubtitleV1) => void;
  isDirty?: boolean;
  applyTimingOffset: (
    deltaMs: number,
    mode?: 'auto' | 'all' | 'fromActive'
  ) => void;
  getActiveIndex: () => number | null;
  saveStatus: 'unsaved' | 'saved-locally' | 'synced' | 'saving';
  lastLocalSave: number | null;
  lastServerSync: number | null;
  documentVersion: number;
  /** Bumped by undo/redo so segment editors show the restored text. */
  historyVersion: number;
  updateDocumentResult: (documentResult: DocumentResult) => void;
  reloadFromServer: (freshDocument: DocumentResult) => void;
}

export interface CaptionProviderProps {
  children: React.ReactNode;
  session?: Session;
  document?: DocumentResult;
}

// useReducer types

export type State = {
  session?: Session;
  document?: DocumentSubtitleV1;
  documentResult?: DocumentResult;
  query: string;
  clickedIndex: number | null;
  undoStack: DocumentSubtitleV1[];
  redoStack: DocumentSubtitleV1[];
  search: string;
  currentMatch: DocumentSubtitleSegmentV1[] | null;
  caseSensitive: boolean;
  wholeWord: boolean;
  isDirty?: boolean;
  saveStatus: 'unsaved' | 'saved-locally' | 'synced' | 'saving';
  lastLocalSave: number | null;
  lastServerSync: number | null;
  documentVersion: number;
};

export type Action = {
  type:
    | 'SET_QUERY'
    | 'SET_SESSION'
    | 'SET_DOCUMENT'
    | 'SET_CLICKED_INDEX'
    | 'SET_UNDO_STACK'
    | 'SET_REDO_STACK'
    | 'SET_DOCUMENT_RESULT'
    | 'SET_SEARCH'
    | 'SET_CURRENT_MATCH'
    | 'SET_CASE_SENSITIVE'
    | 'SET_WHOLE_WORD'
    | 'SET_IS_DIRTY'
    | 'SET_SAVE_STATUS'
    | 'SET_LAST_LOCAL_SAVE'
    | 'SET_LAST_SERVER_SYNC'
    | 'INCREMENT_DOCUMENT_VERSION';
  payload:
    | null
    | number
    | boolean
    | string
    | DocumentSubtitleV1
    | DocumentSubtitleV1[]
    | Session
    | DocumentResult
    | DocumentSubtitleSegmentV1[]
    | DocumentSubtitleSegmentV1
    | string[];
};

export const initialState: State = {
  session: undefined,
  document: undefined,
  query: '',
  clickedIndex: null,
  undoStack: [],
  redoStack: [],
  search: '',
  currentMatch: null,
  caseSensitive: false,
  wholeWord: false,
  isDirty: false,
  saveStatus: 'synced',
  lastLocalSave: null,
  lastServerSync: null,
  documentVersion: 0
};

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_QUERY':
      return {
        ...state,
        query: String(action.payload)
      };
    case 'SET_SESSION':
      return {
        ...state,
        session: action.payload as Session
      };
    case 'SET_DOCUMENT':
      return {
        ...state,
        document: action.payload as DocumentSubtitleV1
      };
    case 'SET_CLICKED_INDEX':
      return {
        ...state,
        clickedIndex: Number(action.payload)
      };
    case 'SET_UNDO_STACK':
      return {
        ...state,
        undoStack: action.payload as DocumentSubtitleV1[]
      };
    case 'SET_REDO_STACK':
      return {
        ...state,
        redoStack: action.payload as DocumentSubtitleV1[]
      };
    case 'SET_DOCUMENT_RESULT':
      return {
        ...state,
        documentResult: action.payload as DocumentResult
      };
    case 'SET_SEARCH':
      return {
        ...state,
        search: action.payload as string
      };
    case 'SET_CURRENT_MATCH':
      return {
        ...state,
        currentMatch: !action.payload
          ? null
          : (action.payload as DocumentSubtitleSegmentV1[])
      };
    case 'SET_CASE_SENSITIVE':
      return {
        ...state,
        caseSensitive: Boolean(action.payload)
      };
    case 'SET_WHOLE_WORD':
      return {
        ...state,
        wholeWord: Boolean(action.payload)
      };
    case 'SET_IS_DIRTY':
      return {
        ...state,
        isDirty: Boolean(action.payload)
      };
    case 'SET_SAVE_STATUS':
      return {
        ...state,
        saveStatus: action.payload as
          | 'unsaved'
          | 'saved-locally'
          | 'synced'
          | 'saving'
      };
    case 'SET_LAST_LOCAL_SAVE':
      return {
        ...state,
        lastLocalSave: action.payload as number | null
      };
    case 'SET_LAST_SERVER_SYNC':
      return {
        ...state,
        lastServerSync: action.payload as number | null
      };
    case 'INCREMENT_DOCUMENT_VERSION':
      return {
        ...state,
        documentVersion: state.documentVersion + 1
      };
    default:
      return state;
  }
}
